/**
 * Channel management and sidebar folders around the shell (featureFlags.channels): the create-channel and browse
 * sheets, the folder editor, "Move to folder…" and the sidebar's notices. Opened from the sidebar "+" menu, row menus
 * and ⌘K.
 */
import { FOLDER_ICONS, Shell, type OpenTarget } from "@bundle/core";
import {
  BrowseChannelsSheet,
  CreateChannelSheet,
  FolderEditorSheet,
  MoveToFolderSheet,
  Toast,
  useBrowseChannels,
  useCreateChannel,
  useSidebar,
  useT,
  useTr,
  useWorkspace,
} from "@bundle/ui";
import { useEffect } from "react";

type Props = {
  creating: boolean;
  browsing: boolean;
  onCloseCreate: () => void;
  onCloseBrowse: () => void;
  /** From the browse sheet. */
  onCreate: () => void;
  open: (url: string, target?: OpenTarget) => void;
};

export function ChannelChrome(p: Props) {
  const t = useT();
  const tr = useTr();
  const ws = useWorkspace();
  const sidebar = useSidebar();
  const create = useCreateChannel();
  const browse = useBrowseChannels();
  const createVm = create.vm;
  const browseVm = browse.vm;

  useEffect(() => {
    if (p.creating) createVm.reset();
  }, [p.creating, createVm]);
  useEffect(() => {
    if (p.browsing) void browseVm.refresh();
  }, [p.browsing, browseVm]);

  const openChannel = (channelId: string) => p.open(Shell.formatUrl({ type: "channel", ws: ws.slug, channelId }));
  const s = sidebar.state;
  return (
    <>
      <CreateChannelSheet
        open={p.creating}
        onClose={p.onCloseCreate}
        vm={create.vm}
        state={create.state}
        onCreated={(id) => {
          p.onCloseCreate();
          openChannel(id);
        }}
      />
      <BrowseChannelsSheet
        open={p.browsing}
        onClose={p.onCloseBrowse}
        vm={browse.vm}
        state={browse.state}
        onOpenChannel={(id) => {
          p.onCloseBrowse();
          openChannel(id);
        }}
        onCreate={p.onCreate}
      />
      <FolderEditorSheet
        editor={s.editor}
        icons={FOLDER_ICONS}
        onName={(name) => sidebar.vm.setEditorName(name)}
        onEmoji={(emoji) => sidebar.vm.setEditorEmoji(emoji)}
        onIcon={(icon) => sidebar.vm.setEditorIcon(icon)}
        onSave={() => sidebar.vm.saveEditor()}
        onClose={() => sidebar.vm.cancelEditor()}
      />
      <MoveToFolderSheet moving={s.moving} onPick={(id) => sidebar.vm.moveTo(id)} onClose={() => sidebar.vm.closeMove()} />
      <Toast open={s.notice !== undefined} onClose={() => sidebar.vm.dismissNotice()} message={s.notice === undefined ? "" : tr(s.notice)} closeLabel={t("common.close")} durationMs={4000} testID="sidebar-notice" />
    </>
  );
}
